import { isSuggestibleAttribute } from '../isSuggestibleValue'
import {
  type WorkflowColumnConfiguration,
  type WorkflowConfigurationFactory,
} from './workflowConfigs'

/* eslint-disable @typescript-eslint/naming-convention */
export const workflowPrioritizedColumns = {
  'measure_and_detect/product_alcohol': {
    brand_name: 1,
    name: 2,
    alcoholic: 3,
    alcohol_by_volume: 4,
    alcohol_type: 5,
    product_category: 6,
    product_category_l1_name: 7,
    product_category_l3_name: 8,
    size: 9,
    unit_count: 10,
    bottle: 11,
    alcohol_rating: 12,
    alcohol_varietal: 13,
  },
  'measure_and_detect/product_availability': {
    brand_name: 3,
    name: 4,
    product_id: 5,
    image_url: 6,
    availability_score: 7,
    visibility_score: 8,
    total: 9,
    created_on: 10,
    generated_at: 11,
  },
  'measure_and_detect/product_brand': {
    brand_id: 1,
    brand_name: 2,
    name: 3,
    image_url: 4,
    description: 5,
    details: 6,
    classified_code: 7,
    classified_type: 8,
  },
  'measure_and_detect/product_characteristics': {
    brand_name: 1,
    name: 2,
    image_url: 3,
    description: 4,
    details: 5,
  },
  'measure_and_detect/product_classification': {
    brand_name: 1,
    name: 2,
    image_url: 3,
    product_category: 4,
    product_category_id: 5,
    product_category_l1_name: 6,
    product_category_l2_name: 7,
    product_category_l3_name: 8,
    product_category_l4_name: 9,
    product_category_l5_name: 10,
    product_category_l6_name: 11,
    product_tags: 12,
  },
  'measure_and_detect/product_details': {
    brand_name: 1,
    name: 2,
    display_name: 3,
    image_url: 4,
    description: 5,
    details: 6,
    product_category: 7,
    product_category_path: 8,
    product_id: 9,
  },
  'measure_and_detect/product_identity': {
    entity_id: 1,
    brand_name: 2,
    name: 3,
    image_url: 4,
    product_classification: 5,
    rrc: 6,
    description: 7,
  },
  'measure_and_detect/product_image': {
    brand_name: 1,
    name: 2,
    image_url: 3,
    classified_code: 4,
    classified_type: 5,
    has_image: 6,
    large_image_url: 7,
    locale_code: 8,
    product_id: 9,
    retailer_image_list: 10,
  },
  'measure_and_detect/product_size': {
    entity_id: 1,
    brand_name: 2,
    name: 3,
    image_url: 4,
    size: 5,
    unit_count: 6,
    link: 7,
  },
  'measure_and_detect/product_other': {
    entity_id: 1,
    brand_name: 2,
    name: 3,
    image_url: 4,
    description: 5,
  },
  'measure_and_detect/other_issue': {
    name: 1,
    description: 2,
    image_url: 3,
  },
  'measure_and_detect/product_taxonomy': {
    brand_name: 1,
    name: 2,
    image_url: 3,
    product_category: 4,
    product_category_id: 5,
    product_category_l1_name: 6,
    product_category_l2_name: 7,
    product_category_l3_name: 8,
    product_category_l4_name: 9,
    product_category_l5_name: 10,
    product_category_l6_name: 11,
    product_tags: 12,
  },
}

const isSuggestibleWorkflowAttribute = (workflowId: string, attributeName: string) => {
  if (workflowId === 'measure_and_detect/product_details') {
    return attributeName === 'name'
  } else if (workflowId === 'measure_and_detect/product_size') {
    return ['size', 'size_uom', 'size_value', 'unit_count', 'unit_uom', 'unit_value'].includes(
      attributeName
    )
  }
  return isSuggestibleAttribute(attributeName)
}

const isFlaggableWorkflowAttribute = (workflowId: string, attributeName: string) => {
  if (workflowId === 'measure_and_detect/product_size') {
    if (attributeName === 'product_id') return false
  }
  return true
}

export const measureAndDetectWorkflowConfigFactory: WorkflowConfigurationFactory = {
  regex: 'measure_and_detect/.*',
  priority: 1,
  build: (workflowId, allowSuggestedValues) => ({
    name: 'measure_and_detect',
    showCombineButton: true,
    showLocaleId: false,
    showDueDate: false,
    showSizeModal: true,
    showReviewerAndAuditorDropdown: true,
    sizeModalConfigs: {
      preFill: true,
      sizeUomPath: {
        type: 'attribute',
        name: 'size_uom',
      },
      sizeValuePath: {
        type: 'attribute',
        name: 'size_value',
      },
      sizePath: {
        type: 'attribute',
        name: 'size',
      },
      unitValuePath: {
        type: 'attribute',
        name: 'unit_value',
      },
      unitUomPath: {
        type: 'attribute',
        name: 'unit_uom',
      },
      unitCountPath: {
        type: 'attribute',
        name: 'unit_count',
      },
    },
    showClassifiedType: false,
    sizeFlagChangeCombined: false,
    getColumnConfiguration: (componentType, componentName) => {
      const ordering = workflowPrioritizedColumns[workflowId]
      if (!ordering) return null

      const configuration: WorkflowColumnConfiguration = {
        componentTypeLayers: [],
        showComponentTypeLabels: false,
        isFlaggable: isFlaggableWorkflowAttribute(workflowId, componentName),
        isSuggestible:
          isSuggestibleWorkflowAttribute(workflowId, componentName) && allowSuggestedValues,
        isVisible: !!ordering[componentName],
        ordering: ordering[componentName],
        isFreezable: false,
      }
      return configuration
    },
  }),
}
